import React from "react";
import { motion } from "framer-motion";
import { MapPin, Clock, Phone, Navigation } from "lucide-react";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay: i * 0.1 },
  }),
};

export default function LocationSection() {
  return (
    <section id="location" data-testid="location-section" className="py-20 sm:py-24 lg:py-32 bg-[#121212]">
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          custom={0}
        >
          <span className="text-[#F59E0B] text-sm font-semibold tracking-[0.3em] uppercase">Find Us</span>
        </motion.div>
        <motion.h2
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-80px" }}
          variants={fadeUp}
          custom={1}
          className="font-['Bebas_Neue'] text-4xl sm:text-5xl lg:text-6xl tracking-tight text-white mt-4 mb-16"
        >
          Our <span className="text-[#F59E0B]">Location</span>
        </motion.h2>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Map */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: "-60px" }}
            variants={fadeUp}
            custom={2}
            className="lg:col-span-3 relative overflow-hidden border border-[#262626] bg-[#0A0A0A] min-h-[350px]"
          >
            <iframe
              title="Core Culture Location"
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3502.0012345!2d77.2090!3d28.6139!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjjCsDM2JzUwLjAiTiA3N8KwMTInMzIuNCJF!5e0!3m2!1sen!2sin!4v1234567890"
              width="100%"
              height="100%"
              style={{ border: 0, minHeight: "350px", filter: "invert(90%) hue-rotate(180deg) contrast(0.9)" }}
              allowFullScreen=""
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </motion.div>

          {/* Info cards */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              custom={3}
              className="p-6 border border-[#262626] bg-[#0A0A0A]"
            >
              <MapPin className="w-5 h-5 text-[#F59E0B] mb-3" />
              <h3 className="font-['Bebas_Neue'] text-xl tracking-wide text-white mb-2">Address</h3>
              <p className="text-sm text-[#A3A3A3] leading-relaxed">
                Core Culture Fitness Studio<br />
                Block C, Connaught Place<br />
                New Delhi, 110001
              </p>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              custom={4}
              className="p-6 border border-[#262626] bg-[#0A0A0A]"
            >
              <Clock className="w-5 h-5 text-[#F59E0B] mb-3" />
              <h3 className="font-['Bebas_Neue'] text-xl tracking-wide text-white mb-2">Hours</h3>
              <div className="space-y-1 text-sm text-[#A3A3A3]">
                <p>Monday – Sunday: <span className="text-white">5:00 AM – 12:00 AM</span></p>
                <p className="text-xs text-[#F59E0B] tracking-wider uppercase mt-2">Open 19 hours, 7 days a week</p>
              </div>
            </motion.div>

            <motion.div
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              custom={5}
              className="p-6 border border-[#262626] bg-[#0A0A0A]"
            >
              <Phone className="w-5 h-5 text-[#F59E0B] mb-3" />
              <h3 className="font-['Bebas_Neue'] text-xl tracking-wide text-white mb-2">Contact</h3>
              <a href="tel:8826033661" className="text-sm text-[#A3A3A3] hover:text-[#F59E0B] transition-colors">
                +91 8826033661
              </a>
              <p className="text-sm text-[#A3A3A3] mt-1">contact@rmwebworks.in</p>
            </motion.div>

            <motion.a
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-60px" }}
              variants={fadeUp}
              custom={6}
              href="https://maps.google.com/?q=Connaught+Place+New+Delhi"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="get-directions-btn"
              className="flex items-center justify-center gap-2 py-4 bg-[#F59E0B] text-black font-semibold text-sm tracking-wider uppercase hover:bg-[#D97706] transition-colors duration-300"
            >
              <Navigation size={16} />
              Get Directions
            </motion.a>
          </div>
        </div>
      </div>
    </section>
  );
}
